import { getTokenAsync } from "../../../hooks/API/getAuthToken";
import { makeHeaders } from "../tools";
import { Articles } from "../types/article";
import {
  PartialMedia,
  MediaResponse,
  MediasAndArticleCountResponse,
  MediasResponse,
  MediaId,
  Notifications,
} from "../types/media";
import { MEDIAS_SERVICE_URL } from "./service.helper";

const BASE_URL = MEDIAS_SERVICE_URL + "/medias";

export const getMediasByStoreId = async (storeId: string) => {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    throw new Error("Could not get a token.");
  }
  const response = await fetch(
    `${BASE_URL}/stores/${encodeURIComponent(storeId)}`,
    {
      method: "GET",
      headers: makeHeaders(token),
    }
  );

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  const data = (await response.json()) as MediasResponse;
  data.medias = data.medias.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true })
  );
  return data;
};

export const getArticlesByMediaId = async (mediaId?: string | undefined) => {
  const token = await getTokenAsync();
  if (!mediaId || !token || typeof token !== "string") {
    return [];
  }
  const response = await fetch(`${BASE_URL}/${mediaId}/articles`, {
    method: "GET",
    headers: makeHeaders(token),
  });

  if (response.status === 404) {
    throw new Error("Network response indicates endpoint was not found.");
  }

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  return (await response.json()).articles as Articles;
};

export const getArticlesByProductArticleId = async (
  mediaId: string,
  productArticleId: string
) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return [];
    }
    const response = await fetch(
      `${BASE_URL}/${mediaId}/articles/product_article_id/${productArticleId}`,
      {
        method: "GET",
        headers: makeHeaders(token),
      }
    );

    if (response.status === 404) return [];

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const articles = (await response.json()) as Articles;
    return articles;
  } catch (error) {
    console.error("Fetch error:", error);
    return [];
  }
};

export const getMediaById = async (
  mediaId: string
): Promise<MediaResponse | null> => {
  try {
    const token = await getTokenAsync();
    if (!mediaId || !token || typeof token !== "string") {
      return null;
    }
    const response = await fetch(`${BASE_URL}/${mediaId}`, {
      method: "GET",
      headers: makeHeaders(token),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const media = (await response.json()) as MediaResponse;
    return media;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getMediasAndArticleCount = async (
  productArticleId: string,
  storeId: string
) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return null;
    }
    const response = await fetch(
      `${BASE_URL}/${encodeURIComponent(storeId)}/articles/${productArticleId}`,
      {
        method: "GET",
        headers: makeHeaders(token),
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }
    const data = (await response.json())
      .mediasAndArticleCount as MediasAndArticleCountResponse[];
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const createMedia = async (mediaData: PartialMedia) => {
  const token = await getTokenAsync();
  if (!token || typeof token !== "string") {
    return null;
  }

  // Make sure id is not set. Should be generated in service.
  delete mediaData.id;

  const response = await fetch(`${BASE_URL}`, {
    method: "POST",
    headers: makeHeaders(token),
    body: JSON.stringify(mediaData),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok.");
  }

  const data = (await response.json()) as MediaId;
  return data;
};

export const updateMedia = async (mediaId: string, mediaData: PartialMedia) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return null;
    }

    const response = await fetch(`${BASE_URL}/${mediaId}`, {
      method: "PUT",
      headers: makeHeaders(token),
      body: JSON.stringify(mediaData),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const data = (await response.json()) as string;
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
  }

  return null;
};

export const deleteMedia = async (mediaId: string) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return null;
    }
    const response = await fetch(`${BASE_URL}/${mediaId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const data = (await response.json()) as string;
    return data;
  } catch (error) {
    console.error("Delete error:", error);
  }

  return null;
};

export const getNotifications = async (storeId: string, countryId: string) => {
  try {
    const token = await getTokenAsync();
    if (!token || typeof token !== "string") {
      return;
    }
    const response = await fetch(
      `${BASE_URL}/${encodeURIComponent(storeId)}/notifications?countryId=${countryId}`,
      {
        method: "GET",
        headers: makeHeaders(token),
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }
    return (await response.json()) as Notifications;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return { alerts: [], taskLinks: [], uniqueEdsDates: [] } as Notifications;
};
